import { useMemo } from 'react'
import { cn } from '@app/lib/cn'
import { formatCount, formatDesi, formatPercent, mm2ToDesi } from './format'
import { LatticeSvg } from './lattice-svg'
import {
  LEATHER_YIELD_FACTOR,
  LIVE_REPEATS,
  LIVE_STRIP_WIDTH_MM,
  pieceScaleKey,
  type EstimatePiece,
  type EstimateResult,
  type MaterialEstimate,
  type PieceScale,
} from './live-calc'
import { StripSvg } from './strip-svg'
import { ZarfSvg } from './zarf-svg'

/**
 * The Yerleşim view — the whole SET's yield, read three ways, each method
 * named for what it assumes and drawn where its number comes from:
 *
 *   Ölçek — parça başına        one piece stamped over a panel on its own
 *                               lattice: the classic per-piece assessment.
 *   Zarf — tek çift, serbest    one pair per material nested freely, read as
 *                               its tight bounding envelope: what ONE çift
 *                               of this material needs.
 *   Şerit — seri üretim         the set laid repeatedly on the fixed
 *                               assessment strip: pairs nesting into each
 *                               other, the production reading.
 *
 * The blocks share one selection and one hover with the rail and the canvas —
 * clicking a placement anywhere IS clicking the piece — and they repeat the
 * LiveCalcCard vocabulary (verim, fire dahil, desi/çift) so picture and card
 * can be read against each other. The strip's own figure, `Kullanılan`, says
 * its length holds LIVE_REPEATS pairs rather than silently mixing bases.
 */

export interface YieldViewProps {
  estimate: EstimateResult | null
  /** The request's pieces — the array every `pieceIndex` here points into. */
  pieces: readonly EstimatePiece[]
  /** Numeral per request piece index, matching the rail's numbering. */
  pieceLabels: readonly string[]
  selectedPiece: number | null
  hoverPiece?: number | null
  onSelectPiece?: (pieceIndex: number) => void
  onHoverPiece?: (pieceIndex: number | null) => void
  className?: string
}

/** DOM id of one material's strip block — the "Yerleşimi gör" scroll target. */
export function yieldStripDomId(materialId: number): string {
  return `saya-yield-strip-${materialId}`
}

const meterFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function YieldView({
  estimate,
  pieces,
  pieceLabels,
  selectedPiece,
  hoverPiece = null,
  onSelectPiece,
  onHoverPiece,
  className,
}: YieldViewProps) {
  if (!estimate || estimate.materials.length === 0) {
    return (
      <div className={cn('flex min-h-40 items-center justify-center', className)}>
        <p className="text-data text-ink-muted">
          {estimate
            ? 'Parçalara malzeme seçin; ölçek, zarf ve şerit yerleşimleri burada çizilir.'
            : 'Canlı tahmin bekleniyor…'}
        </p>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      <ScalePanel
        estimate={estimate}
        pieces={pieces}
        pieceLabels={pieceLabels}
        selectedPiece={selectedPiece}
      />

      <section aria-label="Zarf — tek çift, serbest">
        <p className="eyebrow">Zarf — tek çift, serbest</p>
        <div className="mt-1.5 flex flex-col gap-3">
          {estimate.materials.map((material) => (
            <ZarfBlock
              key={material.materialId}
              material={material}
              selectedPiece={selectedPiece}
              hoverPiece={hoverPiece}
              pieceLabels={pieceLabels}
              onSelectPiece={onSelectPiece}
              onHoverPiece={onHoverPiece}
            />
          ))}
        </div>
      </section>

      <section aria-label={`Şerit — seri üretim (${formatCount(LIVE_STRIP_WIDTH_MM)} mm)`}>
        <p className="eyebrow">Şerit — seri üretim ({formatCount(LIVE_STRIP_WIDTH_MM)} mm)</p>
        <div className="mt-1.5 flex flex-col gap-3">
          {estimate.materials.map((material) => (
            <StripBlock
              key={material.materialId}
              material={material}
              selectedPiece={selectedPiece}
              hoverPiece={hoverPiece}
              pieceLabels={pieceLabels}
              onSelectPiece={onSelectPiece}
              onHoverPiece={onHoverPiece}
            />
          ))}
        </div>
      </section>
    </div>
  )
}

/* ── Ölçek — parça başına ───────────────────────────────────────────────────── */

/** The piece the panel draws, with everything needed to draw it. */
interface ScaleFocus {
  pieceIndex: number
  material: MaterialEstimate
  outlineMm: Float32Array
  scale: PieceScale
}

function scaleFocusFor(
  estimate: EstimateResult,
  pieces: readonly EstimatePiece[],
  pieceIndex: number,
): ScaleFocus | null {
  const piece = pieces[pieceIndex]
  if (!piece || piece.materialId == null) return null
  const material = estimate.materials.find((entry) => entry.materialId === piece.materialId)
  const layout = material?.layout
  if (!material || !layout) return null
  const scale = layout.scale[pieceScaleKey(piece.geomKey, piece.orientation)]
  if (!scale) return null
  const instance =
    layout.instances.find((entry) => entry.pieceIndex === pieceIndex && !entry.mirrored) ??
    layout.instances.find((entry) => entry.pieceIndex === pieceIndex)
  if (!instance) return null
  return { pieceIndex, material, outlineMm: instance.outlineMm, scale }
}

function ScalePanel({
  estimate,
  pieces,
  pieceLabels,
  selectedPiece,
}: {
  estimate: EstimateResult
  pieces: readonly EstimatePiece[]
  pieceLabels: readonly string[]
  selectedPiece: number | null
}) {
  const focus = useMemo(() => {
    if (selectedPiece != null) return scaleFocusFor(estimate, pieces, selectedPiece)
    // No selection: the biggest drawable piece stands in, with a hint below.
    let best: ScaleFocus | null = null
    let bestArea = -1
    for (let index = 0; index < pieces.length; index += 1) {
      const piece = pieces[index] as EstimatePiece
      if (piece.netMm2 <= bestArea) continue
      const candidate = scaleFocusFor(estimate, pieces, index)
      if (candidate) {
        best = candidate
        bestArea = piece.netMm2
      }
    }
    return best
  }, [estimate, pieces, selectedPiece])

  if (!focus) {
    return (
      <section aria-label="Ölçek — parça başına">
        <p className="eyebrow">Ölçek — parça başına</p>
        <p className="mt-1 text-data text-ink-muted">
          {selectedPiece != null
            ? 'Bu parçanın ölçek alanı hesaplanamadı.'
            : 'Bir parça seçin — ölçek yerleşimi burada çizilir.'}
        </p>
      </section>
    )
  }

  const label = pieceLabels[focus.pieceIndex] ?? String(focus.pieceIndex + 1)
  return (
    <section aria-label="Ölçek — parça başına">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <p className="eyebrow">Ölçek — parça başına</p>
        <p className="min-w-0 truncate font-mono text-micro tnum text-ink-muted">
          parça <span className="text-ink">{label}</span> · {focus.material.materialName}
        </p>
        {selectedPiece === null ? (
          <p className="shrink-0 text-micro text-ink-muted">Bir parça seçin</p>
        ) : null}
      </div>
      <div className="mt-1.5 h-44">
        <LatticeSvg outlineMm={focus.outlineMm} scale={focus.scale} className="rounded-xs" />
      </div>
      <p className="mt-1 font-mono text-micro tnum text-ink-muted">
        Ölçek alanı: <span className="text-ink">{formatDesi(mm2ToDesi(focus.scale.perPieceMm2))}</span>{' '}
        desi/adet · Verim <span className="text-ink">{formatPercent(focus.scale.efficiency)}</span> ·
        Açı <span className="text-ink">{formatCount(focus.scale.angleDeg)}°</span>
      </p>
    </section>
  )
}

/* ── Zarf — tek çift, serbest ───────────────────────────────────────────────── */

function ZarfBlock({
  material,
  selectedPiece,
  hoverPiece,
  pieceLabels,
  onSelectPiece,
  onHoverPiece,
}: {
  material: MaterialEstimate
  selectedPiece: number | null
  hoverPiece: number | null
  pieceLabels: readonly string[]
  onSelectPiece?: ((pieceIndex: number) => void) | undefined
  onHoverPiece?: ((pieceIndex: number | null) => void) | undefined
}) {
  const envelope = material.envelope
  return (
    <div data-zarf-material={material.materialId}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <p className="min-w-0 truncate text-data font-medium text-ink">{material.materialName}</p>
        {envelope ? (
          <p className="font-mono text-micro tnum text-ink-muted">
            Toplam: <span className="text-ink">{formatDesi(envelope.grossDesi)}</span> desi/çift ·
            Verim <span className="text-ink">{formatPercent(envelope.efficiency)}</span>
            {envelope.fireDesi != null ? (
              <>
                {' '}
                · Fire dahil: <span className="text-ink">{formatDesi(envelope.fireDesi)}</span>{' '}
                desi (%{formatCount(Math.round((1 - LEATHER_YIELD_FACTOR) * 100))} pay)
              </>
            ) : null}{' '}
            <span className="text-ink-muted/80">(tek = {formatDesi(envelope.grossDesi / 2)})</span>
          </p>
        ) : null}
      </div>
      {envelope ? (
        <div className="mt-1 overflow-x-auto">
          <ZarfSvg
            envelope={envelope}
            selectedPiece={selectedPiece}
            hoverPiece={hoverPiece}
            pieceLabels={pieceLabels}
            onSelect={onSelectPiece}
            onHover={onHoverPiece}
            className="rounded-xs"
          />
        </div>
      ) : (
        <p className="mt-1 text-micro text-ink-muted">
          Bu malzeme için zarf yerleşimi çizilemedi.
        </p>
      )}
    </div>
  )
}

/* ── Şerit — seri üretim ────────────────────────────────────────────────────── */

function StripBlock({
  material,
  selectedPiece,
  hoverPiece,
  pieceLabels,
  onSelectPiece,
  onHoverPiece,
}: {
  material: MaterialEstimate
  selectedPiece: number | null
  hoverPiece: number | null
  pieceLabels: readonly string[]
  onSelectPiece?: ((pieceIndex: number) => void) | undefined
  onHoverPiece?: ((pieceIndex: number | null) => void) | undefined
}) {
  const layout = material.layout
  return (
    <div id={yieldStripDomId(material.materialId)}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <p className="min-w-0 truncate text-data font-medium text-ink">{material.materialName}</p>
        {layout ? (
          <p className="font-mono text-micro tnum text-ink-muted">
            Kullanılan:{' '}
            <span className="text-ink">{meterFormat.format(layout.usedLengthMm / 1000)} m</span> (
            {formatCount(LIVE_REPEATS)} çift) · Verim{' '}
            <span className="text-ink">{formatPercent(layout.efficiency)}</span>
            {material.wasteInclusiveDesi != null ? (
              <>
                {' '}
                · Fire dahil:{' '}
                <span className="text-ink">{formatDesi(material.wasteInclusiveDesi)}</span>{' '}
                desi/çift (%{formatCount(Math.round((1 - LEATHER_YIELD_FACTOR) * 100))} pay)
              </>
            ) : null}
          </p>
        ) : null}
      </div>
      {layout ? (
        <div className="mt-1 overflow-x-auto">
          <StripSvg
            layout={layout}
            selectedPiece={selectedPiece}
            hoverPiece={hoverPiece}
            pieceLabels={pieceLabels}
            onSelect={onSelectPiece}
            onHover={onHoverPiece}
            className="rounded-xs"
          />
        </div>
      ) : (
        <p className="mt-1 text-micro text-ink-muted">
          {material.problem ?? 'Bu malzeme için şerit yerleşimi çizilemedi.'}
        </p>
      )}
    </div>
  )
}
